export type RelinkAssetKind =
  | "background"
  | "gif"
  | "icon"
  | "illustration"
  | "logo"
  | "emoji";

export type RelinkAssetUsage =
  | "auth"
  | "brand"
  | "career"
  | "empty-state"
  | "feedback"
  | "form"
  | "navigation"
  | "occupation"
  | "status";

export type RelinkImageAsset = {
  alt: string;
  sourceName: string;
  kind: Exclude<RelinkAssetKind, "emoji" | "icon">;
  sourceNodeId: string;
  slug: string;
  src: string;
  usage: RelinkAssetUsage[];
};

export type RelinkLayeredAsset = {
  alt: string;
  sourceName: string;
  frame: {
    height: number;
    width: number;
  };
  kind: "illustration";
  layers: RelinkImageAsset[];
  sourceNodeId: string;
  slug: string;
  usage: RelinkAssetUsage[];
};

export type RelinkCatalogAsset = {
  sourceName: string;
  kind: "emoji" | "icon";
  sourceNodeId: string;
  slug: string;
  source: string;
  usage: RelinkAssetUsage[];
};

const questionsBackgroundSimple = new URL(
  "./illustrations/questions-background-simple.svg",
  import.meta.url
).href;
const questionsPlants = new URL(
  "./illustrations/questions-plants.svg",
  import.meta.url
).href;
const questionsMarks = new URL(
  "./illustrations/questions-marks.svg",
  import.meta.url
).href;
const questionsMark = new URL(
  "./illustrations/questions-mark.svg",
  import.meta.url
).href;
const questionsCharacter = new URL(
  "./illustrations/questions-character.svg",
  import.meta.url
).href;
const happyStudent = new URL(
  "./illustrations/happy-student.svg",
  import.meta.url
).href;
const expiredLinkAstronaut = new URL(
  "./illustrations/expired-link-astronaut.png",
  import.meta.url
).href;

const authDesktop = new URL("./backgrounds/auth-desktop.svg", import.meta.url)
  .href;
const landingHero = new URL("./backgrounds/landing-hero.jpg", import.meta.url)
  .href;
const loginIndustrial = new URL(
  "./backgrounds/login-industrial.jpg",
  import.meta.url
).href;
const authMobile = new URL("./backgrounds/auth-mobile.svg", import.meta.url)
  .href;
const registrationProfile = new URL(
  "./backgrounds/registration-profile.png",
  import.meta.url
).href;
const relinkColor = new URL("./logos/relink-color.svg", import.meta.url).href;

const gifMensaje = new URL("./gifs/mensaje.gif", import.meta.url).href;
const gifSubir = new URL("./gifs/subir.gif", import.meta.url).href;
const gifDeAcuerdo = new URL("./gifs/de-acuerdo.gif", import.meta.url).href;
const gifArchivoDanado = new URL("./gifs/archivo-danado.gif", import.meta.url)
  .href;
const gifBuscar = new URL("./gifs/buscar.gif", import.meta.url).href;
const gifChico = new URL("./gifs/chico.gif", import.meta.url).href;
const gifGestion = new URL("./gifs/gestion.gif", import.meta.url).href;
const gifCliente = new URL("./gifs/cliente.gif", import.meta.url).href;
const gifListaDeVerificacion = new URL(
  "./gifs/lista-de-verificacion.gif",
  import.meta.url
).href;
const gifCohete = new URL("./gifs/cohete.gif", import.meta.url).href;
const gifDetener = new URL("./gifs/detener.gif", import.meta.url).href;
const gifRobot = new URL("./gifs/robot.gif", import.meta.url).href;
const gifHabilidadesBlandas = new URL(
  "./gifs/habilidades-blandas.gif",
  import.meta.url
).href;
const gifCarrera = new URL("./gifs/carrera.gif", import.meta.url).href;
const gifBusquedaDeEmpleo = new URL(
  "./gifs/busqueda-de-empleo.gif",
  import.meta.url
).href;
const gifAstronauta = new URL("./gifs/astronauta.gif", import.meta.url).href;

export const relinkBackgrounds = [
  {
    alt: "Trabajadora industrial usando tablet en una planta productiva",
    sourceName: "img-header",
    kind: "background",
    sourceNodeId: "1:17028",
    slug: "landing-hero",
    src: landingHero,
    usage: ["brand", "career"]
  },
  {
    alt: "Background de login y registro para desktop",
    sourceName: "background login / registro",
    kind: "background",
    sourceNodeId: "5353:19410",
    slug: "auth-desktop",
    src: authDesktop,
    usage: ["auth"]
  },
  {
    alt: "Trabajador industrial usando casco y lentes de seguridad",
    sourceName: "gestion-y-seguridad-de-bases-de-datos",
    kind: "background",
    sourceNodeId: "1:15323",
    slug: "login-industrial",
    src: loginIndustrial,
    usage: ["auth"]
  },
  {
    alt: "Background de login y registro para mobile",
    sourceName: "background mobile",
    kind: "background",
    sourceNodeId: "5389:11207",
    slug: "auth-mobile",
    src: authMobile,
    usage: ["auth"]
  },
  {
    alt: "Trabajadora en un taller tecnológico",
    sourceName: "Rectangle",
    kind: "background",
    sourceNodeId: "12:91386",
    slug: "registration-profile",
    src: registrationProfile,
    usage: ["auth", "form"]
  }
] as const satisfies readonly RelinkImageAsset[];

export const relinkLogos = [
  {
    alt: "Relink",
    sourceName: "relink / color",
    kind: "logo",
    sourceNodeId: "5356:1872",
    slug: "relink-color",
    src: relinkColor,
    usage: ["brand"]
  }
] as const satisfies readonly RelinkImageAsset[];

export const relinkIllustrations = [
  {
    alt: "Astronauta junto a un cohete",
    sourceName: "cat-astronaut/rafiki",
    kind: "illustration",
    sourceNodeId: "51:201339",
    slug: "expired-link-astronaut",
    src: expiredLinkAstronaut,
    usage: ["auth", "feedback"]
  },
  {
    alt: "Persona junto a signos de pregunta",
    sourceName: "questions",
    frame: {
      height: 280,
      width: 280
    },
    kind: "illustration",
    layers: [
      {
        alt: "",
        sourceName: "background-simple",
        kind: "illustration",
        sourceNodeId: "7334:20512",
        slug: "questions-background-simple",
        src: questionsBackgroundSimple,
        usage: ["empty-state", "form"]
      },
      {
        alt: "",
        sourceName: "plants",
        kind: "illustration",
        sourceNodeId: "7334:20512",
        slug: "questions-plants",
        src: questionsPlants,
        usage: ["empty-state", "form"]
      },
      {
        alt: "",
        sourceName: "question-marks-2",
        kind: "illustration",
        sourceNodeId: "7334:20512",
        slug: "questions-marks",
        src: questionsMarks,
        usage: ["empty-state", "form"]
      },
      {
        alt: "",
        sourceName: "question-mark-1",
        kind: "illustration",
        sourceNodeId: "7334:20512",
        slug: "questions-mark",
        src: questionsMark,
        usage: ["empty-state", "form"]
      },
      {
        alt: "",
        sourceName: "character",
        kind: "illustration",
        sourceNodeId: "7334:20512",
        slug: "questions-character",
        src: questionsCharacter,
        usage: ["empty-state", "form"]
      }
    ],
    sourceNodeId: "7334:20512",
    slug: "questions",
    usage: ["empty-state", "form"]
  },
  {
    alt: "Estudiante celebrando",
    sourceName: "happy-student",
    kind: "illustration",
    sourceNodeId: "7334:21066",
    slug: "happy-student",
    src: happyStudent,
    usage: ["career", "feedback"]
  }
] as const satisfies readonly (RelinkImageAsset | RelinkLayeredAsset)[];

export const relinkGifs = [
  {
    alt: "Animacion de mensaje",
    sourceName: "mensaje 1",
    kind: "gif",
    sourceNodeId: "7353:1033",
    slug: "mensaje",
    src: gifMensaje,
    usage: ["feedback"]
  },
  {
    alt: "Animacion de subir archivo",
    sourceName: "subir 1",
    kind: "gif",
    sourceNodeId: "7353:1035",
    slug: "subir",
    src: gifSubir,
    usage: ["form"]
  },
  {
    alt: "Animacion de acuerdo",
    sourceName: "de-acuerdo 1",
    kind: "gif",
    sourceNodeId: "7353:1037",
    slug: "de-acuerdo",
    src: gifDeAcuerdo,
    usage: ["feedback", "status"]
  },
  {
    alt: "Animacion de archivo danado",
    sourceName: "archivo-danado 1",
    kind: "gif",
    sourceNodeId: "7353:1039",
    slug: "archivo-danado",
    src: gifArchivoDanado,
    usage: ["feedback", "form"]
  },
  {
    alt: "Animacion de busqueda",
    sourceName: "buscar 1",
    kind: "gif",
    sourceNodeId: "7353:1041",
    slug: "buscar",
    src: gifBuscar,
    usage: ["navigation"]
  },
  {
    alt: "Animacion de persona",
    sourceName: "chico 1",
    kind: "gif",
    sourceNodeId: "7353:1043",
    slug: "chico",
    src: gifChico,
    usage: ["occupation"]
  },
  {
    alt: "Animacion de gestion",
    sourceName: "gestion 1",
    kind: "gif",
    sourceNodeId: "7353:1045",
    slug: "gestion",
    src: gifGestion,
    usage: ["career"]
  },
  {
    alt: "Animacion de cliente",
    sourceName: "cliente 1",
    kind: "gif",
    sourceNodeId: "7353:1047",
    slug: "cliente",
    src: gifCliente,
    usage: ["career"]
  },
  {
    alt: "Animacion de lista de verificacion",
    sourceName: "lista-de-verificacion 1",
    kind: "gif",
    sourceNodeId: "7353:1049",
    slug: "lista-de-verificacion",
    src: gifListaDeVerificacion,
    usage: ["feedback", "form"]
  },
  {
    alt: "Animacion de cohete",
    sourceName: "cohete 1",
    kind: "gif",
    sourceNodeId: "7353:1051",
    slug: "cohete",
    src: gifCohete,
    usage: ["feedback"]
  },
  {
    alt: "Animacion de detener",
    sourceName: "detener 1",
    kind: "gif",
    sourceNodeId: "7353:1053",
    slug: "detener",
    src: gifDetener,
    usage: ["status"]
  },
  {
    alt: "Animacion de robot",
    sourceName: "robot 1",
    kind: "gif",
    sourceNodeId: "7353:1056",
    slug: "robot",
    src: gifRobot,
    usage: ["auth", "feedback"]
  },
  {
    alt: "Animacion de habilidades blandas",
    sourceName: "habilidades-blandas 1",
    kind: "gif",
    sourceNodeId: "7353:1058",
    slug: "habilidades-blandas",
    src: gifHabilidadesBlandas,
    usage: ["career"]
  },
  {
    alt: "Animacion de carrera",
    sourceName: "carrera 1",
    kind: "gif",
    sourceNodeId: "7353:1060",
    slug: "carrera",
    src: gifCarrera,
    usage: ["career"]
  },
  {
    alt: "Animacion de busqueda de empleo",
    sourceName: "busqueda-de-empleo 1",
    kind: "gif",
    sourceNodeId: "7355:1268",
    slug: "busqueda-de-empleo",
    src: gifBusquedaDeEmpleo,
    usage: ["career", "navigation"]
  },
  {
    alt: "Animacion de astronauta",
    sourceName: "astronauta 1",
    kind: "gif",
    sourceNodeId: "7366:3458",
    slug: "astronauta",
    src: gifAstronauta,
    usage: ["career", "feedback"]
  }
] as const satisfies readonly RelinkImageAsset[];

export const relinkIconCatalog = [
  {
    sourceName: "xmark-solid",
    kind: "icon",
    sourceNodeId: "1803:34708",
    slug: "xmark-solid",
    source: "fontawesome",
    usage: ["navigation"]
  },
  {
    sourceName: "user-solid",
    kind: "icon",
    sourceNodeId: "1803:34708",
    slug: "user-solid",
    source: "fontawesome",
    usage: ["auth", "navigation"]
  },
  {
    sourceName: "eye-solid",
    kind: "icon",
    sourceNodeId: "1803:34708",
    slug: "eye-solid",
    source: "fontawesome",
    usage: ["form"]
  },
  {
    sourceName: "eye-slash-solid",
    kind: "icon",
    sourceNodeId: "1803:34708",
    slug: "eye-slash-solid",
    source: "fontawesome",
    usage: ["form"]
  },
  {
    sourceName: "chevron-right-solid",
    kind: "icon",
    sourceNodeId: "1803:34708",
    slug: "chevron-right-solid",
    source: "fontawesome",
    usage: ["navigation"]
  },
  {
    sourceName: "check-solid",
    kind: "icon",
    sourceNodeId: "1803:34708",
    slug: "check-solid",
    source: "fontawesome",
    usage: ["feedback", "status"]
  },
  {
    sourceName: "heroicons-solid:briefcase",
    kind: "icon",
    sourceNodeId: "1803:34708",
    slug: "briefcase",
    source: "heroicons",
    usage: ["career"]
  },
  {
    sourceName: "heroicons-solid:book-open",
    kind: "icon",
    sourceNodeId: "1803:34708",
    slug: "book-open",
    source: "heroicons",
    usage: ["career"]
  },
  {
    sourceName: "heroicons-solid:search",
    kind: "icon",
    sourceNodeId: "1803:34708",
    slug: "search",
    source: "heroicons",
    usage: ["navigation"]
  },
  {
    sourceName: "lucide:file-pen",
    kind: "icon",
    sourceNodeId: "1803:34708",
    slug: "file-pen",
    source: "lucide",
    usage: ["form"]
  }
] as const satisfies readonly RelinkCatalogAsset[];

export const relinkEmojiCatalog = [
  {
    sourceName: "Artist",
    kind: "emoji",
    sourceNodeId: "2327:38130",
    slug: "artist",
    source: "emoji",
    usage: ["occupation"]
  },
  {
    sourceName: "Astronaut",
    kind: "emoji",
    sourceNodeId: "2327:38130",
    slug: "astronaut",
    source: "emoji",
    usage: ["occupation"]
  },
  {
    sourceName: "Construction Worker",
    kind: "emoji",
    sourceNodeId: "2327:38130",
    slug: "construction-worker",
    source: "emoji",
    usage: ["occupation"]
  },
  {
    sourceName: "Cook",
    kind: "emoji",
    sourceNodeId: "2327:38130",
    slug: "cook",
    source: "emoji",
    usage: ["occupation"]
  },
  {
    sourceName: "Health Worker",
    kind: "emoji",
    sourceNodeId: "2327:38130",
    slug: "health-worker",
    source: "emoji",
    usage: ["occupation"]
  },
  {
    sourceName: "Office Worker",
    kind: "emoji",
    sourceNodeId: "2327:38130",
    slug: "office-worker",
    source: "emoji",
    usage: ["occupation"]
  },
  {
    sourceName: "Scientist",
    kind: "emoji",
    sourceNodeId: "2327:38130",
    slug: "scientist",
    source: "emoji",
    usage: ["occupation"]
  },
  {
    sourceName: "Student",
    kind: "emoji",
    sourceNodeId: "2327:38130",
    slug: "student",
    source: "emoji",
    usage: ["occupation"]
  },
  {
    sourceName: "Teacher",
    kind: "emoji",
    sourceNodeId: "2327:38130",
    slug: "teacher",
    source: "emoji",
    usage: ["occupation"]
  },
  {
    sourceName: "Technologist",
    kind: "emoji",
    sourceNodeId: "2327:38130",
    slug: "technologist",
    source: "emoji",
    usage: ["occupation"]
  }
] as const satisfies readonly RelinkCatalogAsset[];

export const relinkAssetGroups = [
  {
    assets: relinkBackgrounds,
    title: "Backgrounds"
  },
  {
    assets: relinkLogos,
    title: "Logos"
  },
  {
    assets: relinkIllustrations,
    title: "Illustrations"
  },
  {
    assets: relinkGifs,
    title: "GIFs"
  },
  {
    assets: relinkIconCatalog,
    title: "Icon catalog"
  },
  {
    assets: relinkEmojiCatalog,
    title: "Emoji catalog"
  }
] as const;
